/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const INITIALIZE_REPUTATION_METRICS_DISCRIMINATOR = new Uint8Array([
  228, 11, 207, 44, 52, 188, 169, 237,
]);

export function getInitializeReputationMetricsDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_REPUTATION_METRICS_DISCRIMINATOR,
  );
}

export type InitializeReputationMetricsInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountReputationMetrics extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountReputationMetrics extends string
        ? WritableAccount<TAccountReputationMetrics>
        : TAccountReputationMetrics,
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeReputationMetricsInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type InitializeReputationMetricsInstructionDataArgs = {};

export function getInitializeReputationMetricsInstructionDataEncoder(): FixedSizeEncoder<InitializeReputationMetricsInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_REPUTATION_METRICS_DISCRIMINATOR,
    }),
  );
}

export function getInitializeReputationMetricsInstructionDataDecoder(): FixedSizeDecoder<InitializeReputationMetricsInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getInitializeReputationMetricsInstructionDataCodec(): FixedSizeCodec<
  InitializeReputationMetricsInstructionDataArgs,
  InitializeReputationMetricsInstructionData
> {
  return combineCodec(
    getInitializeReputationMetricsInstructionDataEncoder(),
    getInitializeReputationMetricsInstructionDataDecoder(),
  );
}

export type InitializeReputationMetricsAsyncInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics?: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  /** System program */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
};

export async function getInitializeReputationMetricsInstructionAsync<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: InitializeReputationMetricsAsyncInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeReputationMetricsInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountSystemProgram,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.reputationMetrics.value) {
    accounts.reputationMetrics.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 112, 117, 116, 97, 116, 105, 111, 110, 95, 109, 101, 116,
            114, 105, 99, 115,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getInitializeReputationMetricsInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeReputationMetricsInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type InitializeReputationMetricsInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  /** System program */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
};

export function getInitializeReputationMetricsInstruction<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: InitializeReputationMetricsInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeReputationMetricsInstruction<
  TProgramAddress,
  TAccountReputationMetrics,
  TAccountAgent,
  TAccountAuthority,
  TAccountSystemProgram,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: false },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getInitializeReputationMetricsInstructionDataEncoder().encode({}),
    programAddress,
  } as InitializeReputationMetricsInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type ParsedInitializeReputationMetricsInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Reputation metrics account */
    reputationMetrics: TAccountMetas[0];
    /** Agent account */
    agent: TAccountMetas[1];
    /** Authority (agent owner) */
    authority: TAccountMetas[2];
    /** System program */
    systemProgram: TAccountMetas[3];
    /** Clock for timestamps */
    clock: TAccountMetas[4];
  };
  data: InitializeReputationMetricsInstructionData;
};

export function parseInitializeReputationMetricsInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeReputationMetricsInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      reputationMetrics: getNextAccount(),
      agent: getNextAccount(),
      authority: getNextAccount(),
      systemProgram: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getInitializeReputationMetricsInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
